import React, { useEffect, useRef, useState } from 'react';
import {
  caretIndexAfterNumericChars,
  countNumericChars,
  finalizeNumberCanonical,
  formatNumberDisplay,
  formatNumberTyping,
} from './numberFieldFormat';

type FormattedNumberInputProps = {
  name: string;
  value: string | number;
  onCanonicalChange: (name: string, canonical: string) => void;
  required?: boolean;
  style?: React.CSSProperties;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  id?: string;
};

const FormattedNumberInput: React.FC<FormattedNumberInputProps> = ({
  name,
  value,
  onCanonicalChange,
  required,
  style,
  onFocus,
  onBlur,
  onKeyDown,
  id,
}) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(() => formatNumberTyping(String(value ?? '')).display);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!focused) {
      setDraft(formatNumberDisplay(value));
    }
  }, [value, focused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    const prev = el.value;
    const caret = el.selectionStart ?? prev.length;
    const numericBefore = countNumericChars(prev.slice(0, caret));
    const { display, canonical } = formatNumberTyping(prev);
    setDraft(display);
    onCanonicalChange(name, canonical);
    requestAnimationFrame(() => {
      const node = inputRef.current;
      if (!node) return;
      const nextCaret = caretIndexAfterNumericChars(display, numericBefore);
      node.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const finalized = finalizeNumberCanonical(draft);
    onCanonicalChange(name, finalized);
    setDraft(formatNumberDisplay(finalized));
    setFocused(false);
    onBlur?.(e);
  };

  return (
    <input
      ref={inputRef}
      id={id}
      name={name}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      required={required}
      value={focused ? draft : formatNumberDisplay(value)}
      onChange={handleChange}
      onFocus={(e) => {
        setFocused(true);
        setDraft(formatNumberDisplay(value) || formatNumberTyping(String(value ?? '')).display);
        onFocus?.(e);
      }}
      onBlur={handleBlur}
      onKeyDown={onKeyDown}
      style={style}
    />
  );
};

export default FormattedNumberInput;
